import * as React from 'react'

import { useDebouncedValue } from '#/shared/lib/hooks'

/** Mirrors the suggestion-search debounce so a match lands with the results. */
const SETTLE_MS = 250

interface UseAutoSelectExactParams<T> {
  /** Match only while the value comes from live typing (not prefill/selection). */
  enabled: boolean
  value: string
  items: T[]
  getLabel: (item: T) => string
  onMatch: (item: T) => void
}

/**
 * Commits a suggestion automatically when the typed text equals exactly one
 * option (case-insensitive), so free typing behaves like picking it from the
 * dropdown. Waits for a typing pause before committing — rewriting the
 * controlled value mid-keystroke can swallow input — and leaves ambiguous
 * matches (same label under several items) to a manual pick.
 */
export function useAutoSelectExact<T>({
  enabled,
  value,
  items,
  getLabel,
  onMatch,
}: UseAutoSelectExactParams<T>) {
  const settledValue = useDebouncedValue(value, SETTLE_MS)
  const isSettled = settledValue === value

  React.useEffect(() => {
    if (!enabled || !isSettled) return
    const query = value.trim().toLowerCase()
    if (!query) return
    const matches = items.filter(
      (item) => getLabel(item).trim().toLowerCase() === query,
    )
    if (matches.length !== 1) return
    onMatch(matches[0])
  }, [enabled, isSettled, value, items, getLabel, onMatch])
}
